import {
  DAY_MULTIPLICATOR,
  HOUR_MULTIPLICATOR,
  MINUTE_MULTIPLICATOR,
  TDelayMultiplicator,
  TMessageSchedule,
} from './programmed-message.type'

/**
 * Type for ISO 8601 duration strings with explicit sign
 * Format: [+|-]P[n]Y[n]M[n]W[n]DT[n]H[n]M[n]S
 *
 * The sign indicates temporal direction:
 * - `+` prefix = future (after the reference date)
 * - `-` prefix = past (before the reference date)
 * - No prefix = defaults to positive/future
 *
 * Examples:
 * - "+PT1H" = 1 hour in the future (after)
 * - "-P1D" = 1 day in the past (before)
 * - "+P1DT2H30M" = 1 day, 2 hours, 30 minutes in the future
 * - "-PT30M" = 30 minutes in the past
 * - "PT0S" = zero duration (no sign)
 */
export type IsoDuration = string

/**
 * Regular expression to parse ISO 8601 duration format
 * Format: P[n]Y[n]M[n]W[n]DT[n]H[n]M[n]S
 * Captures: years, months, weeks, days, hours, minutes, seconds
 */
const ISO_DURATION_REGEX =
  /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/

/**
 * Compares two ISO 8601 durations
 * Returns:
 *  - positive number if duration1 > duration2
 *  - negative number if duration1 < duration2
 *  - 0 if they are equal
 *
 * Takes into account the sign prefix (+/-)
 * Examples:
 * - compareIsoDurations("+P1D", "+PT12H") returns positive (1 day > 12 hours)
 * - compareIsoDurations("-P1D", "+P1D") returns negative (-1 day < +1 day)
 * - compareIsoDurations("+P1W", "+P7D") returns 0 (equal)
 */
export function compareIsoDurations(
  duration1: IsoDuration | undefined,
  duration2: IsoDuration | undefined,
): number {
  if (!duration1 && !duration2) return 0
  if (!duration1) return -1
  if (!duration2) return 1

  const toSeconds = (duration: string): number => {
    const isNegative = duration.startsWith('-')
    const hasSign = duration.startsWith('-') || duration.startsWith('+')
    const normalizedDuration = hasSign ? duration.substring(1) : duration

    const match = normalizedDuration.match(ISO_DURATION_REGEX)

    if (!match) {
      return 0
    }

    const years = Number.parseInt(match[1] || '0', 10)
    const months = Number.parseInt(match[2] || '0', 10)
    const weeks = Number.parseInt(match[3] || '0', 10)
    const days = Number.parseInt(match[4] || '0', 10)
    const hours = Number.parseInt(match[5] || '0', 10)
    const minutes = Number.parseInt(match[6] || '0', 10)

    let total = 0
    total += years * 365.25 * 86400
    total += months * 30 * 86400
    total += weeks * 7 * 86400
    total += days * 86400
    total += hours * 3600
    total += minutes * 60

    return isNegative ? -total : total
  }

  const seconds1 = toSeconds(duration1)
  const seconds2 = toSeconds(duration2)

  return seconds1 - seconds2
}

const getDateValuesFromSchedule = (
  schedule: TMessageSchedule,
): {
  isBefore: boolean
  value: number
  multiplicator: TDelayMultiplicator
} => {
  const durationDiff = schedule.durationDiff
  const isBefore = durationDiff?.startsWith('-') ?? false

  if (!durationDiff) {
    return { isBefore: false, value: 0, multiplicator: MINUTE_MULTIPLICATOR }
  }

  const normalizedDuration = durationDiff.replace(/^[+-]/, '')

  // Extract components from ISO 8601 duration: P[n]Y[n]M[n]W[n]DT[n]H[n]M[n]S
  const match = normalizedDuration.match(ISO_DURATION_REGEX)

  if (!match) {
    return { isBefore: false, value: 0, multiplicator: MINUTE_MULTIPLICATOR }
  }

  const years = Number.parseInt(match[1] || '0', 10)
  const months = Number.parseInt(match[2] || '0', 10)
  const weeks = Number.parseInt(match[3] || '0', 10)
  const days = Number.parseInt(match[4] || '0', 10)
  const hours = Number.parseInt(match[5] || '0', 10)
  const minutes = Number.parseInt(match[6] || '0', 10)

  // Convert everything to total days for comparison
  const totalDays = years * 365 + months * 30 + weeks * 7 + days

  let value = 0
  let multiplicator: TDelayMultiplicator

  if (totalDays > 0) {
    multiplicator = DAY_MULTIPLICATOR
    value = totalDays
  } else if (hours > 0) {
    multiplicator = HOUR_MULTIPLICATOR
    value = hours
  } else {
    multiplicator = MINUTE_MULTIPLICATOR
    value = minutes
  }

  return { isBefore, value, multiplicator }
}

const getRelativeSendingDate = (durationDiff: string | undefined): string => {
  if (!durationDiff) return 'J+00'

  const isBefore = durationDiff.startsWith('-') ? '-' : '+'
  const normalizedDuration = durationDiff.replace(/^[+-]/, '')

  const match = normalizedDuration.match(ISO_DURATION_REGEX)

  if (!match) return 'J+00'

  const years = Number.parseInt(match[1] || '0', 10)
  const months = Number.parseInt(match[2] || '0', 10)
  const weeks = Number.parseInt(match[3] || '0', 10)
  const days = Number.parseInt(match[4] || '0', 10)

  const totalDays = years * 365 + months * 30 + weeks * 7 + days
  return `J${isBefore}${totalDays.toString().padStart(2, '0')}`
}

export { getDateValuesFromSchedule, getRelativeSendingDate }
